import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { ArrowRightLeft, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerHistoryTransfer = () => {
  const [oldEmail, setOldEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/manager-transfer/history?limit=25`, config);
      setHistory(res.data || []);
    } catch (err) {
      toast.error('Failed to load transfer history');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const resetForm = () => {
    setOldEmail('');
    setNewEmail('');
    setPreview(null);
  };

  const handlePreview = async (e) => {
    e.preventDefault();
    setPreview(null);
    setPreviewing(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.post(
        `${API}/admin/manager-transfer/preview`,
        { old_email: oldEmail.trim(), new_email: newEmail.trim() },
        config,
      );
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Move ${preview.records_to_move} performance evaluation(s) from ${preview.old_manager.email} → ${preview.new_manager.email}?\n\nThis cannot be undone automatically.`
    )) return;

    setCommitting(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.post(
        `${API}/admin/manager-transfer/commit`,
        { old_email: oldEmail.trim(), new_email: newEmail.trim() },
        config,
      );
      toast.success(res.data?.message || 'Transfer complete');
      resetForm();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="manager-transfer">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
            Transfer Manager Historical Data
          </CardTitle>
          <CardDescription>
            Reassign past <strong>performance evaluations</strong> from one manager to another. The new manager will start
            seeing those records in their Stack Ranking dashboard. Other collections (leaves, payroll, etc.) are untouched.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePreview} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="old-email">Old manager email</Label>
                <Input
                  id="old-email"
                  type="email"
                  placeholder="shipra.t@greybulltechnologies.com"
                  value={oldEmail}
                  onChange={(e) => setOldEmail(e.target.value)}
                  required
                  data-testid="old-manager-email"
                />
              </div>
              <div>
                <Label htmlFor="new-email">New manager email</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="ayush.a@greybulltechnologies.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  data-testid="new-manager-email"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                disabled={previewing || !oldEmail.trim() || !newEmail.trim()}
                className="bg-slate-700 hover:bg-slate-800"
                data-testid="preview-transfer-btn"
              >
                {previewing ? 'Checking…' : 'Preview transfer'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                data-testid="reset-transfer-btn"
              >
                Reset
              </Button>
            </div>
          </form>

          {preview && (
            <div
              className="mt-6 p-4 rounded-lg border border-emerald-300 bg-emerald-50"
              data-testid="transfer-preview"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-900">
                    {preview.records_to_move} performance evaluation(s) will be moved
                  </div>
                  <div className="text-sm text-emerald-800 mt-1">
                    From <strong>{preview.old_manager.name}</strong> ({preview.old_manager.email})
                    {' '}→ <strong>{preview.new_manager.name}</strong> ({preview.new_manager.email})
                  </div>
                  <div className="text-xs text-emerald-700 mt-1">
                    Of which {preview.submitted_records} are in <code>submitted</code> status (shown on the Stack Ranking dashboard).
                  </div>
                  {preview.sample?.length > 0 && (
                    <div className="mt-3 text-xs text-emerald-900">
                      <div className="font-medium mb-1">Most recent records (sample):</div>
                      <ul className="space-y-0.5 ml-4 list-disc">
                        {preview.sample.map((s, i) => (
                          <li key={i}>
                            {s.evaluation_year}-{String(s.evaluation_month).padStart(2, '0')} · employee {s.employee_id?.slice(0, 8)}… · {s.status}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {preview.records_to_move === 0 && (
                    <div className="mt-2 flex items-center gap-2 text-amber-700 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      Nothing to move — old manager has no performance records.
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <Button
                  onClick={handleCommit}
                  disabled={committing || preview.records_to_move === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="commit-transfer-btn"
                >
                  {committing ? 'Transferring…' : `Confirm — Move ${preview.records_to_move} record(s)`}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Transfer history</span>
            <Button variant="outline" size="sm" onClick={fetchHistory} data-testid="refresh-history-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </CardTitle>
          <CardDescription>Last 25 transfers, newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="transfer-history-empty">
              No transfers yet.
            </p>
          ) : (
            <Table data-testid="transfer-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Records moved</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-sm">
                      {h.created_at ? new Date(h.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {h.changes?.from_manager_name || '—'}
                      <div className="text-xs text-gray-500">{h.changes?.from_manager_email}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {h.changes?.to_manager_name || '—'}
                      <div className="text-xs text-gray-500">{h.changes?.to_manager_email}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{h.changes?.modified_count ?? 0}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{h.performed_by_name || h.performed_by_email || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagerHistoryTransfer;
